import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Clock, Calendar } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { blogPosts } from '../mock';

const renderInline = (line, idx) => {
  // Very small **bold** parser
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  return (
    <p key={idx} className="mt-4 text-[#1F0068]/85 text-[17px] leading-[1.7]">
      {parts.map((p, i) => p.startsWith('**') && p.endsWith('**')
        ? <strong key={i} className="font-bold text-[#1F0068]">{p.slice(2, -2)}</strong>
        : <span key={i}>{p}</span>)}
    </p>
  );
};

const BlogPost = () => {
  const { slug } = useParams();
  const nav = useNavigate();
  const post = blogPosts.find((p) => p.id === slug);
  const related = blogPosts.filter((p) => p.id !== slug).slice(0, 3);

  if (!post) {
    return (
      <div className="bg-dhs min-h-screen">
        <Header />
        <main className="py-20 text-center">
          <p className="text-[#1F0068] font-bold">Article not found.</p>
          <Button className="mt-4 rounded-full" onClick={() => nav('/#blog')}>Back to guides</Button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-dhs min-h-screen">
      <Header />
      <main>
        <article className="pt-8 pb-16">
          <div className="max-w-3xl mx-auto px-5">
            <Link to="/#blog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F0068]/70 hover:text-[#1F0068] mb-4">
              <ArrowLeft className="w-4 h-4" /> Back to guides
            </Link>
            <div className="flex items-center gap-3 text-xs text-[#1F0068]/60 mb-3">
              <span className="inline-flex items-center gap-1 bg-[#FFEDD6] text-[#1F0068] font-bold px-2.5 py-1 rounded-full">{post.category}</span>
              <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {post.date}</span>
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {post.read}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#1F0068] tracking-tight leading-tight">{post.title}</h1>
            <p className="mt-4 text-lg text-[#1F0068]/70">{post.excerpt}</p>
          </div>

          <div className="mt-8 max-w-5xl mx-auto px-5">
            <div className="aspect-[16/8] overflow-hidden rounded-3xl shadow-lg">
              <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
            </div>
          </div>

          <div className="mt-10 max-w-3xl mx-auto px-5">
            {post.body.map((line, i) => renderInline(line, i))}

            <div className="mt-10 bg-[#1F0068] text-white rounded-3xl p-6 md:p-8">
              <div className="text-xs uppercase tracking-widest text-[#FF5C39] font-bold">Ready to protect your home?</div>
              <h3 className="text-2xl font-bold mt-1">Get a quote in under two minutes</h3>
              <p className="mt-2 text-white/80 text-sm">Maintenance service plans for UK households — monthly or annual, cancel any time.</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/quote"><Button className="rounded-full bg-[#FF5C39] hover:bg-[#e0492b] text-white gap-1.5">Get a quote <ArrowRight className="w-4 h-4" /></Button></Link>
                <Link to="/triage"><Button variant="outline" className="rounded-full border-white text-white hover:bg-white hover:text-[#1F0068]">Talk to Homey AI</Button></Link>
              </div>
            </div>
          </div>
        </article>

        <section className="pb-20">
          <div className="max-w-6xl mx-auto px-5">
            <h2 className="text-2xl font-bold text-[#1F0068] mb-6">Keep reading</h2>
            <div className="grid sm:grid-cols-3 gap-5">
              {related.map((p) => (
                <Link key={p.id} to={`/blog/${p.id}`} className="dhs-card group block rounded-2xl overflow-hidden bg-white border border-[#1F0068]/5">
                  <div className="aspect-[16/10] overflow-hidden"><img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                  <div className="p-5">
                    <div className="text-xs text-[#1F0068]/60 font-semibold">{p.category} · {p.date}</div>
                    <div className="font-bold text-[#1F0068] mt-1 leading-snug group-hover:text-[#FF5C39]">{p.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
